import {
    Address,
    AvailableProductsDocument,
    AvailableProductsQuery,
    CheckAvailabilityInput,
    useCheckAvailabilityMutation,
    VerifiedAddressDocument,
    VerifiedAddressQuery,
} from 'gql/__generated/vp-apollo';
import { useCallback } from 'react';
import { useErrorHandling } from './useErrorHandling';
import { ApolloCache, InMemoryCache } from '@apollo/client';
import {
    AaxUnavailableErrorResult,
    InvalidParametersErrorResult,
    NoProductsAvailableErrorResult,
} from '@cobra/fe-lib/client-error-handling/classes/ErrorResult';

export const useCheckAvailability = () => {
    const { error, handleError, resetError } = useErrorHandling<
        NoProductsAvailableErrorResult | AaxUnavailableErrorResult | InvalidParametersErrorResult
    >();

    const [mutate, { data, loading }] = useCheckAvailabilityMutation({
        onError(error) {
            handleError(error);
        },
    });

    const storeVerifiedAddress = useCallback((cache: ApolloCache<InMemoryCache>, address: Address) => {
        cache.writeQuery<VerifiedAddressQuery>({
            query: VerifiedAddressDocument,
            data: {
                verifiedAddress: {
                    __typename: 'Address',
                    ...address,
                },
            },
        });
    }, []);

    const storeAvailableProducts = useCallback((cache: ApolloCache<InMemoryCache>, products: number[]) => {
        cache.writeQuery<AvailableProductsQuery>({
            query: AvailableProductsDocument,
            data: {
                availableProducts: products,
            },
        });
    }, []);

    const checkAvailability = useCallback(
        async (addressInput: CheckAvailabilityInput) => {
            resetError();
            return mutate({
                variables: { input: addressInput },
                update(cache, { data }) {
                    if (data) {
                        const { address, products } = data.checkAvailability;

                        storeVerifiedAddress(cache, {
                            ...addressInput,
                            areaCode: address.areaCode,
                        });

                        storeAvailableProducts(cache, products);
                    }
                },
            });
        },
        [mutate, resetError, storeVerifiedAddress, storeAvailableProducts]
    );

    return {
        checkAvailability,
        availability: data?.checkAvailability || null,
        loading,
        error,
    };
};
